<script>
/**
 * @component DiagnosisCatalog
 * @description Pantalla de administración del maestro de diagnósticos.
 *   Incluye listado paginado con filtros (búsqueda, clasificación, destacados),
 *   CRUD vía dialog, toggle de destacados y dialog de importación masiva CIE-10
 *   con parseo de Excel en frontend (SheetJS) y envío bulk al backend.
 * @example <DiagnosisCatalog />
 */
import diagnosisCatalogService from "@/services/diagnosis-catalog";
import Spinner from "@/components/ui/Spinner";
import debounce from "debounce";
import { mapGetters } from "vuex";

const DEBOUNCE_MS = 300;

const CLASSIFICATION_OPTIONS = [
  { value: "cie10", label: "CIE-10" },
  { value: "snomed", label: "SNOMED" },
  { value: "custom", label: "Custom" },
];

export default {
  name: "DiagnosisCatalog",

  components: { Spinner },

  data() {
    return {
      isLoading: false,
      searchInput: "",
      filterClassification: "",
      filterFeatured: false,
      diagnoses: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      classificationOptions: CLASSIFICATION_OPTIONS,

      // Dialog crear/editar
      isFormVisible: false,
      isFormLoading: false,
      formMode: "create",
      form: this.emptyForm(),

      // Dialog importar
      isImportVisible: false,
      isImporting: false,
      importFile: null,
      importPreview: [],
      importResult: null,
    };
  },

  computed: {
    ...mapGetters(["isAdmin"]),

    /**
     * @description Título del dialog de crear/editar
     * @returns {string}
     */
    formTitle() {
      return this.formMode === "create"
        ? "Nuevo diagnóstico"
        : "Editar diagnóstico";
    },
  },

  created() {
    this.fetchDiagnoses();
  },

  methods: {
    /**
     * @description Retorna un objeto de formulario vacío
     * @returns {Object}
     */
    emptyForm() {
      return {
        code: "",
        description: "",
        classification: "cie10",
        chapter: "",
        category: "",
        isFeatured: false,
      };
    },

    /**
     * @description Obtiene los diagnósticos del backend con filtros y paginación
     */
    async fetchDiagnoses() {
      this.isLoading = true;
      try {
        const params = {
          offset: (this.currentPage - 1) * this.pageSize,
          limit: this.pageSize,
        };
        if (this.searchInput) params.searchInput = this.searchInput;
        if (this.filterClassification)
          params.classification = this.filterClassification;
        if (this.filterFeatured) params.isFeatured = true;

        const result = await diagnosisCatalogService.getAll(params);
        this.diagnoses = result.docs;
        this.total = result.total;
      } catch (error) {
        this.$message.error("Error al cargar diagnósticos");
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    debounceSearch: debounce(function () {
      this.currentPage = 1;
      this.fetchDiagnoses();
    }, DEBOUNCE_MS),

    onFilterChange() {
      this.currentPage = 1;
      this.fetchDiagnoses();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchDiagnoses();
    },

    /**
     * @description Devuelve el label legible de una clasificación
     * @param {string} value - Valor de clasificación
     * @returns {string}
     */
    getClassificationLabel(value) {
      const opt = CLASSIFICATION_OPTIONS.find((o) => o.value === value);
      return opt ? opt.label : value;
    },

    // --- CRUD ---

    /**
     * @description Abre el dialog para crear un nuevo diagnóstico
     */
    openCreate() {
      this.formMode = "create";
      this.form = this.emptyForm();
      this.isFormVisible = true;
    },

    /**
     * @description Abre el dialog para editar un diagnóstico existente
     * @param {Object} diagnosis - Diagnóstico a editar
     */
    openEdit(diagnosis) {
      this.formMode = "edit";
      this.form = {
        _id: diagnosis._id,
        code: diagnosis.code || "",
        description: diagnosis.description || "",
        classification: diagnosis.classification || "cie10",
        chapter: diagnosis.chapter || "",
        category: diagnosis.category || "",
        isFeatured: !!diagnosis.isFeatured,
      };
      this.isFormVisible = true;
    },

    closeForm() {
      this.isFormVisible = false;
    },

    /**
     * @description Guarda el diagnóstico (crea o actualiza según el modo)
     */
    async saveForm() {
      if (!this.form.code || !this.form.description) {
        this.$message.warning("Código y Descripción son requeridos");
        return;
      }
      this.isFormLoading = true;
      try {
        const payload = {
          code: this.form.code,
          description: this.form.description,
          classification: this.form.classification,
          chapter: this.form.chapter,
          category: this.form.category,
          isFeatured: this.form.isFeatured,
        };
        if (this.formMode === "create") {
          await diagnosisCatalogService.create(payload);
          this.$message.success("Diagnóstico creado");
        } else {
          await diagnosisCatalogService.update(this.form._id, payload);
          this.$message.success("Diagnóstico actualizado");
        }
        this.closeForm();
        this.fetchDiagnoses();
      } catch (error) {
        const msg =
          error.response?.data?.error || "Error al guardar el diagnóstico";
        this.$message.error(msg);
      } finally {
        this.isFormLoading = false;
      }
    },

    /**
     * @description Elimina un diagnóstico previa confirmación
     * @param {Object} diagnosis - Diagnóstico a eliminar
     */
    async deleteDiagnosis(diagnosis) {
      try {
        await this.$confirm(
          `¿Desea eliminar el diagnóstico "${diagnosis.description}" (${diagnosis.code})?`,
          "Eliminar",
          {
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await diagnosisCatalogService.delete(diagnosis._id);
        this.$message.success("Diagnóstico eliminado");
        this.fetchDiagnoses();
      } catch {
        // Cancelado
      }
    },

    /**
     * @description Alterna el estado destacado de un diagnóstico
     * @param {Object} diagnosis - Diagnóstico
     */
    async toggleFeatured(diagnosis) {
      try {
        await diagnosisCatalogService.toggleFeatured(diagnosis._id);
        diagnosis.isFeatured = !diagnosis.isFeatured;
      } catch (error) {
        this.$message.error("Error al actualizar destacado");
        console.error(error);
      }
    },

    // --- Importación masiva ---

    /**
     * @description Abre el dialog de importación masiva CIE-10
     */
    openImport() {
      this.isImportVisible = true;
      this.importFile = null;
      this.importPreview = [];
      this.importResult = null;
    },

    closeImport() {
      this.isImportVisible = false;
      this.importFile = null;
      this.importPreview = [];
      this.importResult = null;
    },

    /**
     * @description Captura el archivo Excel y genera preview de las primeras 10 filas
     * @param {Event} event - Evento del input file
     */
    async onFileSelected(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.importFile = file;
      this.importPreview = [];
      this.importResult = null;

      try {
        const XLSX = await import("xlsx");
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        this.importPreview = rows.slice(0, 10).map((row) => ({
          code: String(
            row["Código"] || row["codigo"] || row["code"] || row["CODIGO"] || ""
          ).trim(),
          description: String(
            row["Descripción"] ||
              row["descripcion"] ||
              row["description"] ||
              row["DESCRIPCION"] ||
              ""
          ).trim(),
          chapter: String(
            row["Capítulo"] || row["capitulo"] || row["chapter"] || ""
          ).trim(),
          category: String(
            row["Categoría"] || row["categoria"] || row["category"] || ""
          ).trim(),
        }));

        this._parsedRows = rows;
      } catch (error) {
        this.$message.error("Error al leer el archivo Excel");
        console.error(error);
        this.importFile = null;
      }
    },

    /**
     * @description Parsea todas las filas del Excel, las mapea y envía al endpoint bulk.
     *   Muestra progreso y resultado (importados, actualizados, errores).
     */
    async executeImport() {
      if (!this.importFile || !this._parsedRows) {
        this.$message.warning("Seleccioná un archivo primero");
        return;
      }

      this.isImporting = true;
      this.importResult = null;

      try {
        const diagnoses = this._parsedRows
          .map((row) => ({
            code: String(
              row["Código"] ||
                row["codigo"] ||
                row["code"] ||
                row["CODIGO"] ||
                ""
            ).trim(),
            description: String(
              row["Descripción"] ||
                row["descripcion"] ||
                row["description"] ||
                row["DESCRIPCION"] ||
                ""
            ).trim(),
            classification: "cie10",
            chapter: String(
              row["Capítulo"] || row["capitulo"] || row["chapter"] || ""
            ).trim(),
            category: String(
              row["Categoría"] || row["categoria"] || row["category"] || ""
            ).trim(),
          }))
          .filter((d) => d.code && d.description);

        if (diagnoses.length === 0) {
          this.$message.warning(
            "No se encontraron filas válidas. Verificá que el archivo tenga columnas 'Código' y 'Descripción'."
          );
          return;
        }

        const result = await diagnosisCatalogService.bulkImport(diagnoses);
        this.importResult = {
          total: diagnoses.length,
          imported: result.imported || 0,
          updated: result.updated || 0,
          errors: result.errors || [],
        };

        if (result.imported > 0 || result.updated > 0) {
          this.$message.success(
            `Importación: ${result.imported || 0} insertados, ${
              result.updated || 0
            } actualizados`
          );
          this.fetchDiagnoses();
        }
      } catch (error) {
        const msg =
          error.response?.data?.error || "Error al importar diagnósticos";
        this.$message.error(msg);
      } finally {
        this.isImporting = false;
      }
    },
  },
};
</script>

<template lang="pug">
  section
    header.headline
      .headline__title Diagnósticos
      .headline__actions
        el-input.search__input(
          prefix-icon="el-icon-search"
          v-model="searchInput"
          @input="debounceSearch"
          placeholder="Buscar por código o descripción..."
          :clearable="true"
          size="small"
        )
        el-select(
          v-model="filterClassification"
          placeholder="Clasificación"
          clearable
          @change="onFilterChange"
          size="small"
        )
          el-option(
            v-for="opt in classificationOptions"
            :key="opt.value"
            :label="opt.label"
            :value="opt.value"
          )
        el-switch.featured-toggle(
          v-model="filterFeatured"
          active-text="Solo destacados"
          @change="onFilterChange"
        )
        .button.button--outline(@click="openImport()" v-if="isAdmin")
          micon(name="cloud_upload").button__icon
          span.button__text Importar CIE-10
        .button.button--blue(@click="openCreate()" v-if="isAdmin")
          micon(name="add_circle_outline").button__icon
          span.button__text + Nuevo diagnóstico

    .box.box--with-subnav
      .box__content--stretch
        table
          thead
            tr
              th Código
              th Descripción
              th Clasificación
              th.th--center Destacado
              th Acciones
          tbody
            spinner(v-if="isLoading")
            tr(
              v-else
              v-for="diagnosis in diagnoses"
              :key="diagnosis._id"
              @click="openEdit(diagnosis)"
            )
              td {{ diagnosis.code }}
              td {{ diagnosis.description }}
              td {{ getClassificationLabel(diagnosis.classification) }}
              td.td--center(@click.stop)
                micon.star-icon(
                  :name="diagnosis.isFeatured ? 'star' : 'star_border'"
                  :class="{ 'star-icon--active': diagnosis.isFeatured }"
                  @click.native="toggleFeatured(diagnosis)"
                )
              td.actions
                .actions-container(@click.stop)
                  button.button.button--action(@click="openEdit(diagnosis)")
                    micon(name="edit")
                  button.button.button--action(@click="deleteDiagnosis(diagnosis)" v-if="isAdmin")
                    micon(name="delete_forever")

      el-pagination(
        v-if="total > pageSize"
        layout="prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :current-page="currentPage"
        @current-change="onPageChange"
      )

    //- Dialog crear/editar
    el-dialog(:title="formTitle" :visible.sync="isFormVisible" width="600px")
      el-form(label-position="top" size="small")
        el-row(:gutter="16")
          el-col(:span="12")
            el-form-item(label="Código")
              el-input(v-model="form.code" placeholder="Ej: A00.0")
          el-col(:span="12")
            el-form-item(label="Clasificación")
              el-select(v-model="form.classification" placeholder="Clasificación")
                el-option(
                  v-for="opt in classificationOptions"
                  :key="opt.value"
                  :label="opt.label"
                  :value="opt.value"
                )
        el-form-item(label="Descripción")
          el-input(v-model="form.description" placeholder="Descripción del diagnóstico" type="textarea" :rows="2")
        el-row(:gutter="16")
          el-col(:span="12")
            el-form-item(label="Capítulo")
              el-input(v-model="form.chapter" placeholder="Ej: Capítulo I")
          el-col(:span="12")
            el-form-item(label="Categoría")
              el-input(v-model="form.category" placeholder="Ej: Enfermedades infecciosas")
        el-form-item(label="Destacado")
          el-switch(v-model="form.isFeatured")

      template(#footer)
        el-button(@click="closeForm") Cancelar
        el-button(type="primary" @click="saveForm" :loading="isFormLoading") Guardar

    //- Dialog importar CIE-10
    el-dialog(title="Importar diagnósticos CIE-10" :visible.sync="isImportVisible" width="700px")
      .import-form
        p.import-help El archivo Excel debe contener columnas #[strong Código] y #[strong Descripción]. Columnas opcionales: Capítulo, Categoría. Se importan como clasificación CIE-10.

        el-form-item(label="Archivo Excel (.xlsx)")
          input.file-input(
            type="file"
            accept=".xlsx,.xls"
            @change="onFileSelected"
          )

        //- Preview de primeras filas
        template(v-if="importPreview.length > 0")
          p.import-preview-label Vista previa (primeras {{ importPreview.length }} filas):
          .import-preview
            table.import-preview__table
              thead
                tr
                  th Código
                  th Descripción
                  th Capítulo
                  th Categoría
              tbody
                tr(v-for="(row, idx) in importPreview" :key="idx")
                  td {{ row.code }}
                  td {{ row.description }}
                  td {{ row.chapter }}
                  td {{ row.category }}

        //- Resultado de la importación
        .import-result(v-if="importResult")
          el-alert(
            :title="`Importación finalizada: ${importResult.imported} insertados, ${importResult.updated} actualizados de ${importResult.total} registros`"
            :type="importResult.errors.length === 0 ? 'success' : 'warning'"
            :closable="false"
            show-icon
          )
          .import-errors(v-if="importResult.errors.length > 0")
            p.import-errors__title Errores:
            ul
              li(v-for="(err, idx) in importResult.errors" :key="idx") {{ err }}

      template(#footer)
        el-button(@click="closeImport") Cerrar
        el-button(
          type="primary"
          @click="executeImport"
          :loading="isImporting"
          :disabled="!importFile"
        ) Importar
</template>

<style lang="scss" scoped>
.headline {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.headline__title {
  font-size: 20px;
  font-weight: 700;
  color: $navy;
}

.headline__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.search__input {
  width: 280px;
}

.el-select {
  width: 160px;
}

.featured-toggle {
  margin-left: 4px;
}

.button--outline {
  background: transparent;
  border: 1px solid $navy;
  color: $navy;
}

.button--outline:hover {
  background: rgba(0, 45, 94, 0.06);
}

tbody td {
  cursor: pointer;
}

.th--center,
.td--center {
  text-align: center;
}

.star-icon {
  cursor: pointer;
  width: 20px;
  height: 20px;
  fill: #c0c4cc;
  transition: fill 0.15s ease;

  &--active {
    fill: #f5a623;
  }

  &:hover {
    fill: #f5a623;
  }
}

/* ── Import dialog ── */
.import-form {
  padding: 0 5px;
}

.import-help {
  margin-bottom: 16px;
  color: #606266;
  font-size: 13px;
  line-height: 1.5;
}

.file-input {
  width: 100%;
  padding: 8px 0;
}

.import-preview-label {
  font-size: 13px;
  font-weight: 600;
  color: $navy;
  margin: 16px 0 8px;
}

.import-preview {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.import-preview__table {
  width: 100%;
  font-size: 12px;

  th {
    background: #f5f7fa;
    padding: 8px;
    text-align: left;
    font-weight: 600;
    color: #606266;
  }

  td {
    padding: 6px 8px;
    border-top: 1px solid #ebeef5;
    cursor: default;
  }
}

.import-result {
  margin-top: 16px;
}

.import-errors {
  margin-top: 10px;
  max-height: 150px;
  overflow-y: auto;
  font-size: 12px;
  color: #f56c6c;
}

.import-errors__title {
  font-weight: bold;
  margin-bottom: 4px;
}

.import-errors ul {
  padding-left: 20px;
}
</style>
